"use client";

import { useCallback } from "react";
import { ComposeFormValues } from "@/lib/types/compose-modal-types";
import { TicketSummary } from "@/lib/types/database";
import { useConnectionSignatures } from "@/lib/hooks/api/emails/email-signatures";
import {
  buildFollowUpTaskData,
  appendSignatureToBody,
} from "@/lib/utils/crm/compose-modal-utils";
import {
  useFollowUpTaskContext,
  FollowUpTaskMessageData,
} from "../contexts/FollowUpTaskContext";
import { useVariableContext } from "../contexts/VariableContext";
import type { ComposeHookState } from "./compose-form-types";

/** The slice of compose state the submit resolver reads. */
export interface ComposeSubmitFormAccess {
  form: Pick<ComposeHookState["form"], "watch">;
  selectedClient: ComposeHookState["selectedClient"];
  recipients: ComposeHookState["recipients"];
  ccRecipients: ComposeHookState["ccRecipients"];
  bccRecipients: ComposeHookState["bccRecipients"];
  emailConnections: ComposeHookState["emailConnections"];
}

interface UseComposeSubmitResolverArgs {
  composeForm: ComposeSubmitFormAccess;
  selectedTicket?: TicketSummary | null;
  /** The surface's own send handler, called with the fully resolved values. */
  onSubmit: (values: ComposeFormValues) => void;
  /** Draft cleanup — the draft is only dropped once the send is handed off. */
  clearSavedDraft: () => void;
}

/**
 * Owns ONE concern: turning raw form values into the message that actually goes
 * out, in the order those steps have to happen.
 *
 * 1. Append the selected signature to the body. This runs FIRST so a signature
 *    containing `{{variables}}` is resolved by step 2 rather than shipped raw.
 * 2. Resolve template variables across subject and body against the selected
 *    client and ticket.
 * 3. Publish the resolved copy to the follow-up-task context, so a task created
 *    off this send quotes what the recipient will read, not the template.
 * 4. Drop the saved draft — the work is now in flight, not abandoned.
 *
 * The caller's `onSubmit` receives the original values with only `subject` and
 * `body` replaced by their resolved forms.
 */
export function useComposeSubmitResolver({
  composeForm,
  selectedTicket,
  onSubmit,
  clearSavedDraft,
}: UseComposeSubmitResolverArgs): (values: ComposeFormValues) => void {
  const { resolveTemplate } = useVariableContext();
  const { setMessageData } = useFollowUpTaskContext();
  const { data: signatures = [] } = useConnectionSignatures(
    composeForm.form.watch("fromAccount")
  );

  return useCallback(
    (values: ComposeFormValues) => {
      let bodyWithSignature = values.body || "";
      if (values.signature) {
        const signature = signatures.find((s) => s.id === values.signature);
        bodyWithSignature = appendSignatureToBody(bodyWithSignature, signature);
      }

      const result = resolveTemplate(
        values.subject || "",
        bodyWithSignature,
        composeForm.selectedClient || undefined,
        selectedTicket || undefined
      );

      const msgData: FollowUpTaskMessageData = buildFollowUpTaskData(
        values,
        composeForm.selectedClient,
        composeForm.recipients,
        composeForm.ccRecipients,
        composeForm.bccRecipients,
        composeForm.emailConnections,
        result.subject,
        result.body
      );

      setMessageData(msgData);

      clearSavedDraft();

      onSubmit({
        ...values,
        subject: result.subject,
        body: result.body,
      });
    },
    [
      signatures,
      resolveTemplate,
      composeForm.selectedClient,
      composeForm.recipients,
      composeForm.ccRecipients,
      composeForm.bccRecipients,
      composeForm.emailConnections,
      selectedTicket,
      setMessageData,
      onSubmit,
      clearSavedDraft,
    ]
  );
}
